/**
 * Submission guards for DynamicFormRenderer surfaces — US-080/081 parity for
 * the dynamic form engine.
 *
 * Every surface that submits dynamic-form data must:
 *   1. gate the submit on `blockSubmitOnValidationErrors` (inline errors +
 *      scroll, never a toast — the US-080 contract), and
 *   2. route mutation failures through `handleDynamicFormSubmitError` so a
 *      server 400's field details land inline on the renderer while anything
 *      else gets exactly one toast (the US-081 contract; the submit hooks set
 *      FORM_MUTATION_META so the global MutationCache handler stays silent).
 */
import { isFormSchemaV2, validateForm } from "@livingsyncs/types";
import { notifications } from "@mantine/notifications";
import {
  extractApiError,
  scrollToFirstError,
} from "../../utils/formErrorHandler";

/**
 * Pre-submit gate. Returns true when the submit must be blocked — the caller
 * flips `showValidationErrors` on so the renderer surfaces the inline errors
 * this same render. Legacy (non-v2) schemas are not client-validatable and
 * pass through.
 */
export function blockSubmitOnValidationErrors(
  schema: unknown,
  values: Record<string, unknown>,
): boolean {
  if (!isFormSchemaV2(schema)) return false;
  const errors = validateForm(schema, values);
  if (Object.keys(errors).length === 0) return false;
  scrollToFirstError();
  return true;
}

/**
 * Map-or-toast for dynamic form submit failures. A 400 with field `details`
 * lands inline (via the renderer's `serverErrors` prop); everything else gets
 * a single toast with the most specific message available.
 */
export function handleDynamicFormSubmitError(
  error: unknown,
  setServerErrors: (errors: Record<string, string[]>) => void,
  fallback: string = "Failed to submit form",
): void {
  const details = (
    error as { response?: { data?: { details?: Record<string, string[]> } } }
  )?.response?.data?.details;

  if (details && Object.keys(details).length > 0) {
    setServerErrors(details);
    scrollToFirstError();
    return;
  }

  notifications.show({
    title: "Error",
    message: extractApiError(error, fallback),
    color: "red",
  });
}
